import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { KATEGORIER } from '../components/CategorySelector';
import { FYLKER_MED_BYER, getFylkeName, getByName } from '../data/norwegianCities';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EditJobPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location_fylke: '',
    location_city: '',
    price: '',
    pricing_type: 'fixed',
    hourly_rate: ''
  });

  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/logg-inn');
      return;
    }
    fetchJob();
  }, [id, isAuthenticated, navigate]);

  const fetchJob = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobData = response.data;
      
      // Check if user owns this job
      if (jobData.poster?.id !== user?.id) {
        navigate('/');
        return;
      }

      setJob(jobData);
      setFormData({
        title: jobData.title || '',
        description: jobData.description || '',
        category: jobData.category || '',
        location_fylke: jobData.location_fylke || '',
        location_city: jobData.location_city || '',
        price: jobData.price || '',
        pricing_type: jobData.pricing_type || 'fixed',
        hourly_rate: jobData.hourly_rate || ''
      });
      
      if (jobData.images && jobData.images.length > 0) {
        setExistingImages(jobData.images);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      setError('Kunne ikke laste oppdraget');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.slice(0, 5 - (existingImages.length + images.length));
    
    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Update job info
      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        location_fylke: formData.location_fylke,
        location_city: formData.location_city || null,
        price: parseFloat(formData.price) || 0,
        pricing_type: formData.pricing_type,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };

      await axios.put(`${API_URL}/jobs/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Upload new images if any
      if (images.length > 0) {
        const imageFormData = new FormData();
        images.forEach((img) => {
          imageFormData.append('images', img.file);
        });
        imageFormData.append('primaryImageIndex', primaryImageIndex);
        
        // Note: This would require a separate endpoint for adding images
        // For now, we'll just update the basic info
      }

      navigate(`/annonse/${id}`);
    } catch (error) {
      console.error('Error updating job:', error);
      setError(error.response?.data?.message || 'Kunne ikke oppdatere oppdraget');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block animate-spin">progress_activity</span>
          <p className="text-slate-500">Laster oppdrag...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">error</span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Oppdrag ikke funnet
          </h2>
          <Link to="/" className="text-[#1E3A5F] hover:underline">
            Gå tilbake
          </Link>
        </div>
      </div>
    );
  }

  const currentFylke = formData.location_fylke 
    ? FYLKER_MED_BYER.find(f => f.id === formData.location_fylke) 
    : null;
  const byer = currentFylke?.byer || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-14 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(`/annonse/${id}`)}
              className="flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] transition-colors p-2 -ml-2"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="hidden md:inline text-sm">Tilbake</span>
            </button>
            <h1 className="font-heading font-semibold text-[#0A1929]">
              Rediger oppdrag
            </h1>
            <div className="w-16"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tittel */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Tittel *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                maxLength={100}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                placeholder="F.eks. Trenger hjelp med hagearbeid"
              />
              <p className="text-xs text-slate-400 mt-1">{formData.title.length}/100</p>
            </div>

            {/* Beskrivelse */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Beskrivelse *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={5}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent resize-none"
                placeholder="Beskriv oppdraget i detalj..."
              />
            </div>

            {/* Kategori */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Kategori *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {KATEGORIER.map((kategori) => (
                  <button
                    key={kategori.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, category: kategori.id }))}
                    className={`flex flex-col items-center gap-3 p-4 rounded-xl transition-all ${
                      formData.category === kategori.id
                        ? 'bg-[#0A1929] text-white shadow-lg scale-105'
                        : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:shadow-md'
                    }`}
                  >
                    <span className="material-symbols-outlined text-3xl">{kategori.icon}</span>
                    <span className="text-sm font-medium text-center">{kategori.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Lokasjon */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Fylke *
              </label>
              <select
                name="location_fylke"
                value={formData.location_fylke}
                onChange={(e) => setFormData(prev => ({ ...prev, location_fylke: e.target.value, location_city: '' }))}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
              >
                <option value="">Velg fylke</option>
                {FYLKER_MED_BYER.map(fylke => (
                  <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                ))}
              </select>
            </div>

            {formData.location_fylke && byer.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  By *
                </label>
                <select
                  name="location_city"
                  value={formData.location_city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Velg by</option>
                  {byer.map(by => (
                    <option key={by.id} value={by.id}>{by.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Pris */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Pris (NOK) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="50"
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="0"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                  kr
                </span>
              </div>
            </div>

            {/* Eksisterende bilder */}
            {existingImages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Eksisterende bilder
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {existingImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <div className="rounded-lg overflow-hidden aspect-square">
                        <img 
                          src={img.startsWith('http') ? img : `${API_URL.replace('/api', '')}${img}`} 
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(i, true)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nye bilder */}
            {images.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Nye bilder
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {images.map((img, i) => (
                    <div key={i} className="relative group">
                      <div className="rounded-lg overflow-hidden aspect-square">
                        <img 
                          src={img.preview} 
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(i, false)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legg til bilder */}
            {(existingImages.length + images.length) < 5 && (
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Legg til bilder (valgfritt)
                </label>
                <label className="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#1E3A5F] hover:bg-slate-50 transition-all">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-slate-400">add_photo_alternate</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[#0A1929] font-medium">Klikk for å laste opp bilder</span>
                    <p className="text-xs text-slate-400 mt-1">(Maks {5 - (existingImages.length + images.length)} bilder til)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {/* Submit button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(`/annonse/${id}`)}
                className="flex-1 bg-slate-100 text-[#0A1929] py-3 rounded-xl font-medium hover:bg-slate-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#0A1929] text-white py-3 rounded-xl font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="animate-spin material-symbols-outlined">progress_activity</span>
                    Lagrer...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">save</span>
                    Lagre endringer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditJobPage;
